import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { AddMemberToOrganizationDto } from "../../../models/organization/dto/invite-to-organization.dto";
import { OrganizationMemberDto } from "../../../models/organization/dto/organization.dto";
import { GET_SELECTED_ORGANIZATION_VIEW_QUERY_KEY } from "./useGetCurrentOrganization";

/**
 * The request body for `POST organizations/:id/members`. `organizationId` is a
 * path param, NOT a body field — the endpoint's ValidationPipe runs
 * forbidNonWhitelisted. `notify: false` is only sent when set, so an org
 * admin's request is byte-identical to before (it is platform-admin-only and
 * the API answers 403 to anyone else who sends it).
 */
export function toAddMemberBody(body: AddMemberToOrganizationDto) {
    return {
        email: body.email,
        ...(body.role ? { role: body.role } : {}),
        ...(body.notify === false ? { notify: false } : {}),
    };
}

/**
 * Directly add an operator (admin) to an organization by email — no invitation,
 * immediate membership. On success we invalidate the selected-organization view
 * so the new member appears in the list right away.
 */
export const useAddMemberToOrganization = (organizationId: string, onSuccess: (member: OrganizationMemberDto) => void) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['addMemberToOrganization', organizationId],
        mutationFn: (body: AddMemberToOrganizationDto) => apiClient.fetchApi<OrganizationMemberDto, ReturnType<typeof toAddMemberBody>>({ method: 'POST', endpoint: `organizations/${body.organizationId}/members`, body: toAddMemberBody(body) }),
        onSuccess: (member) => {
            onSuccess(member);
            queryClient.invalidateQueries({ queryKey: GET_SELECTED_ORGANIZATION_VIEW_QUERY_KEY(organizationId) });
        },
    });
};
